/**
 * Single-matter PMS import — provider-agnostic client helpers.
 *
 * Every backend route below is already parameterised by provider
 * (`/customer-integrations/pms/:provider/…`), so the import flow needs one implementation, not one per
 * PMS. The provider is an argument here for exactly that reason; what differs between providers is
 * which *inputs* the import needs, and that lives in the UI's provider config, not in the transport.
 *
 * Throws on failure (`DirectApiError`) rather than returning a result envelope — the `client.ts`
 * convention for `directApiClient`-backed helpers.
 */

'use client';

import { directApiClient } from '@/lib/api/direct-client';
import type {
  ImportSingleMatterRequest,
  ImportSingleMatterResponse,
  ImportSingleMatterStatus,
  MatterLookupResult,
  PmsMatterContact,
  PmsProvider
} from './types';

/** Single matter import polling interval in milliseconds */
export const IMPORT_SINGLE_POLL_INTERVAL_MS = 2000;

function pmsRoute(provider: PmsProvider, path: string): string {
  return `/customer-integrations/pms/${provider}/${path}`;
}

/**
 * Look up one matter by its reference number in the PMS.
 *
 * The backend applies an **exact** match on the reference, so this either finds the matter or 404s —
 * it is not a search.
 */
export async function lookupPmsMatterByReference(
  provider: PmsProvider,
  referenceNumber: string
): Promise<MatterLookupResult> {
  return directApiClient.get<MatterLookupResult>(
    pmsRoute(provider, 'matters/lookup'),
    { params: { referenceNumber } }
  );
}

/**
 * Look up candidate matters by client name — every match, for the caller to present as a list.
 *
 * Only meaningful for a PMS that actually indexes matters by client. Providers whose config declares
 * no `name` lookup mode never call this.
 */
export async function lookupPmsMattersByClientName(
  provider: PmsProvider,
  clientName: string
): Promise<MatterLookupResult[]> {
  const data = await directApiClient.get<{ matches: MatterLookupResult[] }>(
    pmsRoute(provider, 'matters/lookup-by-name'),
    { params: { clientName } }
  );

  return data.matches;
}

/**
 * The named people and companies on a PMS matter, for identifying its decedent before import.
 *
 * Returns `[]` for a provider whose matters already identify their own decedent (Smokeball) — an
 * empty list, not an error, so a caller can ask unconditionally and simply render no picker.
 */
export async function getPmsMatterContacts(
  provider: PmsProvider,
  externalMatterId: string
): Promise<PmsMatterContact[]> {
  const data = await directApiClient.get<{
    contacts: PmsMatterContact[];
  }>(
    pmsRoute(
      provider,
      `matters/${encodeURIComponent(externalMatterId)}/contacts`
    )
  );

  return data.contacts;
}

/**
 * Import one matter into Vale.
 *
 * Note the two distinct failure shapes: a rejected *request* throws (400 — an unknown matter type, a
 * decedent who is not on the matter), while a *declined* import resolves with
 * `{ success: false, skipped: true, error }` (200 — the matter or the firm's configuration is missing
 * something). Callers must handle both; only the second carries an explanation a user can act on
 * without changing their input.
 */
export async function importSinglePmsMatter(
  provider: PmsProvider,
  request: ImportSingleMatterRequest
): Promise<ImportSingleMatterResponse> {
  return directApiClient.post<ImportSingleMatterResponse>(
    pmsRoute(provider, 'matters/import-single'),
    request
  );
}

/** Whether a given PMS matter has landed in Vale yet. */
export async function getPmsImportSingleStatus(
  provider: PmsProvider,
  externalMatterId: string
): Promise<ImportSingleMatterStatus> {
  return directApiClient.get<ImportSingleMatterStatus>(
    pmsRoute(
      provider,
      `matters/import-single/${encodeURIComponent(externalMatterId)}/status`
    )
  );
}
